import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  ScrollView,
  Image,
  Clipboard,
  Dimensions,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  Linking,
} from 'react-native';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { useAI } from '../../contexts/AIContext';

import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';
import { analytics, AnalyticsEvent } from '../../services/analytics';
const logger = createLogger('TotpSetup');

const { width } = Dimensions.get('window');

// App Store URLs for authenticator apps
const APP_STORE_URLS = {
  authy: 'https://apps.apple.com/app/authy/id494168017',
  googleAuth: 'https://apps.apple.com/app/google-authenticator/id388497605',
  microsoftAuth: 'https://apps.apple.com/app/microsoft-authenticator/id983156458',
};

interface TotpSetupScreenProps {
  navigation: NavigationProp<any>;
  route: RouteProp<any, any>;
}

const TotpSetupScreen: React.FC<TotpSetupScreenProps> = ({ navigation, route }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [showWhatIsThis, setShowWhatIsThis] = useState(false);
  const [skipsRemaining, setSkipsRemaining] = useState<number>(
    (route.params as any)?.totpSkipsRemaining ?? 0
  );
  const [isSkipping, setIsSkipping] = useState(false);

  const inputRefs = useRef<Array<TextInput | null>>([]);
  const { openChat } = useAI();

  useEffect(() => {
    analytics.track(AnalyticsEvent.TotpSetupShown);
    setupTotp();
  }, []);

  const setupTotp = async () => {
    try {
      let token = await getAuthToken();
      if (!token) {
        token = await getAuthToken();
      }

      if (!token) {
        Alert.alert('Error', 'Authentication required. Please log in again.');
        navigation.reset({
          index: 0,
          routes: [{ name: 'Auth', state: { routes: [{ name: 'Login' }] } }],
        });
        return;
      }

      logger.debug('Calling setup-totp with token:', token?.substring(0, 20) + '...');

      const response = await fetch(`${API_URL}/auth/setup-totp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      logger.debug('setup-totp response status:', response.status);
      const data = await response.json();
      logger.debug('setup-totp response data:', data);

      if (response.ok) {
        setQrCode(data.qrCode);
        setSecret(data.secret);
        setBackupCodes(data.backupCodes);
        if (data.totpSkipsRemaining !== undefined) {
          setSkipsRemaining(data.totpSkipsRemaining);
        }
      } else {
        logger.error('TOTP setup failed:', response.status, data);
        if (response.status === 401 || response.status === 403) {
          Alert.alert(
            'Session Expired',
            'Your session has expired. Please log in again.',
            [
              {
                text: 'OK',
                onPress: () => {
                  navigation.reset({
                    index: 0,
                    routes: [{ name: 'Auth', state: { routes: [{ name: 'Login' }] } }],
                  });
                },
              },
            ]
          );
        } else {
          Alert.alert('Error', data.message || 'Failed to setup TOTP');
          navigation.goBack();
        }
      }
    } catch (error) {
      logger.error('TOTP setup error:', error);
      Alert.alert('Error', 'Failed to setup authenticator. Please try again.');
      navigation.goBack();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (text: string, index: number) => {
    if (text && !/^\d$/.test(text)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = text;
    setCode(newCode);

    if (text && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (index === 5 && text) {
      const fullCode = [...newCode.slice(0, 5), text].join('');
      handleVerify(fullCode);
    }
  };

  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (verificationCode?: string) => {
    const codeToVerify = verificationCode || code.join('');

    if (codeToVerify.length !== 6) {
      Alert.alert('Invalid Code', 'Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);

    try {
      let token = await getAuthToken();
      if (!token) {
        token = await getAuthToken();
      }

      if (!token) {
        Alert.alert('Error', 'Authentication required. Please log in again.');
        navigation.reset({
          index: 0,
          routes: [{ name: 'Auth', state: { routes: [{ name: 'Login' }] } }],
        });
        return;
      }

      const response = await fetch(`${API_URL}/auth/verify-totp-setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: codeToVerify }),
      });

      const data = await response.json();

      if (response.ok) {
        await AsyncStorage.setItem('@2fa_setup_complete', 'true');
        await AsyncStorage.setItem('@email_verified', 'true');
        analytics.track(AnalyticsEvent.TotpSetupCompleted);

        // Check for pending invitation
        const pendingInvitationToken = await AsyncStorage.getItem('@invitation_token');

        Alert.alert(
          'Success',
          'Two-factor authentication has been enabled successfully!',
          [
            {
              text: 'OK',
              onPress: () => {
                if (pendingInvitationToken) {
                  // Redirect to AcceptInvitation screen to complete invitation flow
                  logger.debug('Pending invitation found, redirecting to AcceptInvitation...');
                  navigation.reset({
                    index: 0,
                    routes: [{
                      name: 'AcceptInvitation',
                      params: { token: pendingInvitationToken }
                    }],
                  });
                } else {
                  navigation.replace('Splash');
                }
              },
            },
          ]
        );
      } else {
        Alert.alert('Invalid Code', 'The code you entered is incorrect. Please try again.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      logger.error('TOTP verification error:', error);
      Alert.alert('Error', 'Failed to verify code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSkip = async () => {
    if (skipsRemaining <= 0) return;

    setIsSkipping(true);
    try {
      let token = await getAuthToken();
      if (!token) {
        token = await getAuthToken();
      }

      const response = await fetch(`${API_URL}/auth/skip-totp-setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        setSkipsRemaining(data.skipsRemaining);
        await AsyncStorage.setItem('@2fa_setup_complete', 'true');
        await AsyncStorage.setItem('@totp_skipped', 'true');
        analytics.track(AnalyticsEvent.TotpSetupSkipped, { skips_remaining: data.skipsRemaining });
        navigation.replace('Splash');
      } else {
        Alert.alert('Error', data.message || 'Failed to skip setup');
      }
    } catch (error) {
      logger.error('Skip TOTP error:', error);
      Alert.alert('Error', 'Failed to skip setup. Please try again.');
    } finally {
      setIsSkipping(false);
    }
  };

  const copySecret = () => {
    Clipboard.setString(secret);
    Alert.alert('Copied', 'Secret key copied to clipboard');
  };

  const copyBackupCodes = () => {
    Clipboard.setString(backupCodes.join('\n'));
    Alert.alert('Copied', 'Backup codes copied to clipboard');
  };

  const openAppStore = (url: string) => {
    Linking.openURL(url).catch(() => {
      Alert.alert('Error', 'Could not open the App Store. Please search for the app manually.');
    });
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#29BFC0" />
        <View style={styles.headerSection}>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.title}>Add extra security</Text>
          <Text style={styles.subtitle}>
            Use an authenticator app to keep your vault safe — even if your password leaks.
          </Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>Setting up authenticator...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#29BFC0" />
      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header Section with Teal Background */}
        <View style={styles.headerSection}>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.title}>Add extra security</Text>
          <Text style={styles.subtitle}>
            Use an authenticator app to keep your vault safe — even if your password leaks.
          </Text>
        </View>

        {/* Content Section */}
        <View style={styles.contentSection}>

          {/* "What is this?" expandable section */}
          <TouchableOpacity
            style={styles.whatIsThisToggle}
            onPress={() => setShowWhatIsThis(!showWhatIsThis)}
          >
            <Text style={styles.whatIsThisToggleText}>
              {showWhatIsThis ? 'Hide explanation' : 'What is this?'}
            </Text>
            <Text style={styles.chevronText}>{showWhatIsThis ? '\u25B2' : '\u25BC'}</Text>
          </TouchableOpacity>

          {showWhatIsThis && (
            <View style={styles.whatIsThisCard}>
              <Text style={styles.whatIsThisText}>
                An authenticator app generates a new security code every 30 seconds. This adds an extra layer of protection beyond your password — so even if someone learns your password, they still can't access your account without your phone.
              </Text>
              <TouchableOpacity onPress={openChat} style={styles.askOrionLink}>
                <Text style={styles.askOrionText}>Still have questions? Ask Orion</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Step 1: Download an authenticator app */}
          <View style={styles.stepCard}>
            <View style={styles.stepHeader}>
              <View style={styles.stepBadge}>
                <Text style={styles.stepBadgeText}>1</Text>
              </View>
              <Text style={styles.stepTitle}>Download an authenticator app</Text>
            </View>

            <TouchableOpacity
              style={styles.appLink}
              onPress={() => openAppStore(APP_STORE_URLS.authy)}
            >
              <View style={styles.appLinkContent}>
                <Text style={styles.appLinkName}>Authy</Text>
                <Text style={styles.appLinkDesc}>Recommended for beginners — has cloud backup</Text>
              </View>
              <Text style={styles.appLinkChevron}>{'\u203A'}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.appLink}
              onPress={() => openAppStore(APP_STORE_URLS.googleAuth)}
            >
              <View style={styles.appLinkContent}>
                <Text style={styles.appLinkName}>Google Authenticator</Text>
                <Text style={styles.appLinkDesc}>Simple and widely used</Text>
              </View>
              <Text style={styles.appLinkChevron}>{'\u203A'}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.appLink, styles.appLinkLast]}
              onPress={() => openAppStore(APP_STORE_URLS.microsoftAuth)}
            >
              <View style={styles.appLinkContent}>
                <Text style={styles.appLinkName}>Microsoft Authenticator</Text>
                <Text style={styles.appLinkDesc}>Reliable with backup features</Text>
              </View>
              <Text style={styles.appLinkChevron}>{'\u203A'}</Text>
            </TouchableOpacity>
          </View>

          {/* Step 2: Copy this key into the app */}
          <View style={styles.stepCard}>
            <View style={styles.stepHeader}>
              <View style={styles.stepBadge}>
                <Text style={styles.stepBadgeText}>2</Text>
              </View>
              <Text style={styles.stepTitle}>Copy this key into the app</Text>
            </View>

            <Text style={styles.stepInstruction}>
              Open your authenticator, tap '+' or 'Add Account', choose 'Enter setup key', and paste this code.
            </Text>

            <View style={styles.emailNotice}>
              <Text style={styles.emailNoticeText}>
                Important: The email in your authenticator app must match your MyFinalKey login email.
              </Text>
            </View>

            <TouchableOpacity onPress={copySecret} style={styles.secretCard}>
              <Text style={styles.secretText}>{secret}</Text>
              <View style={styles.copyRow}>
                <Image
                  source={require('../../assets/icons/copy.png')}
                  style={styles.copyIcon}
                />
                <Text style={styles.copyText}>Tap to copy</Text>
              </View>
            </TouchableOpacity>

            {/* QR Code toggle */}
            <TouchableOpacity
              style={styles.qrToggle}
              onPress={() => setShowQrCode(!showQrCode)}
            >
              <Text style={styles.qrToggleText}>
                {showQrCode ? 'Hide QR Code' : 'Show QR Code (for second device)'}
              </Text>
            </TouchableOpacity>

            {showQrCode && qrCode && (
              <View style={styles.qrCard}>
                <Image
                  source={{ uri: qrCode }}
                  style={styles.qrCode}
                  resizeMode="contain"
                />
              </View>
            )}
          </View>

          {/* Step 3: Enter the 6-digit code */}
          <View style={styles.stepCard}>
            <View style={styles.stepHeader}>
              <View style={styles.stepBadge}>
                <Text style={styles.stepBadgeText}>3</Text>
              </View>
              <Text style={styles.stepTitle}>Enter the 6-digit code</Text>
            </View>

            <Text style={styles.stepInstruction}>
              Your app will show a code that changes every 30 seconds. Enter it below.
            </Text>

            <View style={styles.codeContainer}>
              {code.map((digit, index) => (
                <TextInput
                  key={index}
                  ref={(ref) => (inputRefs.current[index] = ref)}
                  style={styles.codeInput}
                  value={digit}
                  onChangeText={(text) => handleCodeChange(text, index)}
                  onKeyPress={(e) => handleKeyPress(e, index)}
                  keyboardType="number-pad"
                  maxLength={1}
                  autoFocus={index === 0}
                  editable={!isVerifying}
                />
              ))}
            </View>

            {/* Verify Button */}
            <TouchableOpacity
              style={[styles.verifyButton, isVerifying && styles.verifyButtonDisabled]}
              onPress={() => handleVerify()}
              disabled={isVerifying || code.join('').length !== 6}
            >
              {isVerifying ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                <Text style={styles.verifyButtonText}>Verify and Continue</Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Backup Codes Toggle */}
          <TouchableOpacity
            style={styles.backupCodesToggle}
            onPress={() => setShowBackupCodes(!showBackupCodes)}
          >
            <Text style={styles.backupCodesToggleText}>
              {showBackupCodes ? 'Hide Backup Codes' : 'View Backup Codes'}
            </Text>
          </TouchableOpacity>

          {/* Backup Codes Card */}
          {showBackupCodes && (
            <View style={styles.backupCodesCard}>
              <Text style={styles.backupCodesTitle}>Backup Codes</Text>
              <Text style={styles.backupCodesWarning}>
                Save these codes in a secure place. Each can only be used once if you lose access to your authenticator app.
              </Text>
              <View style={styles.backupCodesList}>
                {backupCodes.map((backupCode, index) => (
                  <View key={index} style={styles.backupCodeRow}>
                    <Text style={styles.backupCodeText}>{backupCode}</Text>
                  </View>
                ))}
              </View>
              <TouchableOpacity
                style={styles.copyAllButton}
                onPress={copyBackupCodes}
              >
                <Text style={styles.copyAllButtonText}>Copy All Codes</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Skip button — only shown when skips remain */}
          {skipsRemaining > 0 ? (
            <>
              <TouchableOpacity
                style={[styles.skipButton, isSkipping && styles.skipButtonDisabled]}
                onPress={handleSkip}
                disabled={isSkipping}
              >
                {isSkipping ? (
                  <ActivityIndicator color="#29BFC0" size="small" />
                ) : (
                  <Text style={styles.skipButtonText}>
                    Skip for now ({skipsRemaining} {skipsRemaining === 1 ? 'skip' : 'skips'} remaining)
                  </Text>
                )}
              </TouchableOpacity>
              <Text style={styles.skipHint}>You can set up two-factor authentication later in Settings</Text>
            </>
          ) : (
            <Text style={styles.requiredHint}>
              Two-factor authentication is required to continue.
            </Text>
          )}
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  headerSection: {
    backgroundColor: '#29BFC0',
    paddingTop: ms(60),
    paddingBottom: ms(30),
    paddingHorizontal: ms(24),
    alignItems: 'center',
  },
  logo: {
    width: s(80),
    height: s(80),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#29BFC0',
  },
  loadingText: {
    marginTop: ms(16),
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
  },
  contentSection: {
    flex: 1,
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(20),
    paddingBottom: ms(40),
  },

  // "What is this?" section
  whatIsThisToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: ms(12),
    marginBottom: ms(8),
  },
  whatIsThisToggleText: {
    fontSize: ms(30, 0.3),
    color: '#FFFFFF',
    fontWeight: '600',
    marginRight: ms(6),
    textDecorationLine: 'underline',
  },
  chevronText: {
    fontSize: ms(12, 0.3),
    color: '#FFFFFF',
  },
  whatIsThisCard: {
    backgroundColor: '#1C2C4F',
    borderRadius: ms(16),
    padding: ms(20),
    marginBottom: ms(16),
  },
  whatIsThisText: {
    fontSize: ms(14, 0.3),
    color: '#FFFFFF',
    lineHeight: ms(22, 0.3),
    marginBottom: ms(12),
  },
  askOrionLink: {
    alignSelf: 'flex-start',
  },
  askOrionText: {
    fontSize: ms(14, 0.3),
    color: '#F2994A',
    fontWeight: '600',
  },

  // Step cards
  stepCard: {
    backgroundColor: '#1C2C4F',
    borderRadius: ms(16),
    padding: ms(20),
    marginBottom: ms(20),
  },
  stepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(14),
  },
  stepBadge: {
    width: s(30),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#F2994A',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: ms(12),
  },
  stepBadgeText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  stepTitle: {
    fontSize: ms(17, 0.3),
    fontWeight: '700',
    color: '#FFFFFF',
    flex: 1,
  },
  stepInstruction: {
    fontSize: ms(14, 0.3),
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: ms(21, 0.3),
    marginBottom: ms(14),
  },

  // App Store links
  appLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: ms(14),
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.15)',
  },
  appLinkLast: {
    borderBottomWidth: 0,
  },
  appLinkContent: {
    flex: 1,
  },
  appLinkName: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: ms(2),
  },
  appLinkDesc: {
    fontSize: ms(13, 0.3),
    color: 'rgba(255, 255, 255, 0.6)',
  },
  appLinkChevron: {
    fontSize: ms(24, 0.3),
    color: 'rgba(255, 255, 255, 0.4)',
    marginLeft: ms(8),
  },

  // Email match notice
  emailNotice: {
    backgroundColor: 'rgba(255, 159, 90, 0.15)',
    borderLeftWidth: 3,
    borderLeftColor: '#FF9F5A',
    borderRadius: ms(8),
    padding: ms(12),
    marginBottom: ms(14),
  },
  emailNoticeText: {
    fontSize: ms(13, 0.3),
    fontWeight: '600',
    color: '#FF9F5A',
    lineHeight: ms(19, 0.3),
  },

  // Secret key card
  secretCard: {
    backgroundColor: '#E8F4F4',
    borderRadius: ms(12),
    padding: ms(16),
    alignItems: 'center',
  },
  secretText: {
    fontSize: ms(12, 0.3),
    fontFamily: 'monospace',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(12),
    letterSpacing: 0.5,
  },
  copyRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  copyIcon: {
    width: s(18),
    height: s(18),
    marginRight: ms(6),
    tintColor: '#29BFC0',
  },
  copyText: {
    fontSize: ms(14, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
  },

  // QR Code toggle
  qrToggle: {
    paddingVertical: ms(10),
    alignItems: 'center',
  },
  qrToggleText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: ms(14, 0.3),
    textDecorationLine: 'underline',
  },
  qrCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    padding: ms(16),
    alignItems: 'center',
    marginTop: ms(8),
  },
  qrCode: {
    width: s(200),
    height: s(200),
  },

  // Code entry
  codeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(20),
    paddingHorizontal: ms(4),
  },
  codeInput: {
    width: s(48),
    height: s(48),
    borderRadius: ms(24),
    fontSize: ms(20, 0.3),
    textAlign: 'center',
    backgroundColor: '#FFFFFF',
    color: '#1C2C4F',
    fontWeight: '600',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },

  // Verify button
  verifyButton: {
    backgroundColor: '#F2994A',
    paddingVertical: ms(16),
    borderRadius: ms(30),
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
  verifyButtonDisabled: {
    opacity: 0.6,
  },
  verifyButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
  },

  // Backup codes
  backupCodesToggle: {
    paddingVertical: ms(14),
    alignItems: 'center',
  },
  backupCodesToggleText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  backupCodesCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(16),
    padding: ms(20),
    marginTop: ms(16),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  backupCodesTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  backupCodesWarning: {
    fontSize: ms(14, 0.3),
    color: '#F2994A',
    marginBottom: ms(16),
    lineHeight: ms(20, 0.3),
  },
  backupCodesList: {
    marginBottom: ms(16),
  },
  backupCodeRow: {
    backgroundColor: '#F5F5F5',
    borderRadius: ms(8),
    paddingVertical: ms(12),
    paddingHorizontal: ms(16),
    marginBottom: ms(8),
  },
  backupCodeText: {
    fontSize: ms(16, 0.3),
    fontFamily: 'monospace',
    color: '#1C2C4F',
  },
  copyAllButton: {
    backgroundColor: '#F2994A',
    paddingVertical: ms(14),
    borderRadius: ms(30),
    alignItems: 'center',
  },
  copyAllButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },

  // Skip button
  skipButton: {
    marginTop: ms(24),
    padding: ms(16),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    alignItems: 'center',
  },
  skipButtonDisabled: {
    opacity: 0.6,
  },
  skipButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  skipHint: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: ms(12, 0.3),
    textAlign: 'center',
    marginTop: ms(8),
    marginBottom: ms(20),
  },
  requiredHint: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: ms(13, 0.3),
    textAlign: 'center',
    marginTop: ms(16),
    marginBottom: ms(20),
    fontStyle: 'italic',
  },
});

export default TotpSetupScreen;
